import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Brain, Users, Eye, Zap, BookOpen, Heart, Target } from "lucide-react";
import { Helmet } from "react-helmet-async";

const tocItems = [
  { id: "social-accountability", title: "You Focus Better When Someone Can See You" },
  { id: "social-facilitation", title: "Seeing Others Study Makes Your Brain Fall in Line" },
  { id: "body-doubling", title: "Studying Together Doesn't Mean Being Distracted" },
  { id: "pre-commitment", title: "It Makes Starting Way Easier (and Kills Procrastination)" },
  { id: "stress-reduction", title: "It Actually Lowers Stress" },
  { id: "csw", title: "So Where Does CSW Come In?" },
  { id: "takeaway", title: "The Takeaway" },
  { id: "references", title: "References" },
];

const WhyStudyingOnCameraHelps = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Does studying on camera really improve focus?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, research shows that social accountability—knowing your actions are observable—increases effort levels and decreases distractions. Studies on goal completion demonstrate that people are significantly more likely to follow through when commitments are visible to others."
        }
      },
      {
        "@type": "Question",
        "name": "What is social facilitation and how does it help studying?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Social facilitation is a psychological phenomenon where people perform familiar tasks better when others around them are doing the same thing. Research shows productivity improvements of 16–32% when individuals work in the presence of peers, even virtually."
        }
      },
      {
        "@type": "Question",
        "name": "What is body doubling and why is it effective for studying?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Body doubling is the practice of working alongside someone silently. Studies show that students studying with a virtual companion performed as well or better than those studying with someone physically present, while experiencing less pressure and stress."
        }
      },
      {
        "@type": "Question",
        "name": "How does camera-on studying reduce procrastination?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Camera-on studying creates pre-commitment and scheduled intentions, which dramatically increase follow-through. When people decide when and where they'll work with others, they're far more likely to begin and stick with their study sessions."
        }
      },
      {
        "@type": "Question",
        "name": "Can studying with others online reduce stress?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, research on study streaming communities shows that virtual co-study environments reduce feelings of loneliness and help students regulate stress while maintaining focus. Even minimal social presence satisfies a basic psychological need for connection."
        }
      },
      {
        "@type": "Question",
        "name": "What is a Study With Me video and does it work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Study With Me (SWM) videos are recordings of people studying that others watch while working. Large-scale studies found that students report improved concentration and longer focus when studying alongside visible peers, even with no direct interaction."
        }
      },
      {
        "@type": "Question",
        "name": "Is studying on camera as effective as studying in a library?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Camera-on virtual study rooms replicate the key productivity benefits of libraries—social accountability and visible presence—without requiring physical travel. The research suggests virtual co-presence can be equally effective for focus and motivation."
        }
      },
      {
        "@type": "Question",
        "name": "What platforms offer camera-on study rooms?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "CSW (csw.live) offers camera-on study rooms with silent co-presence, structured focus timers, and real students studying simultaneously. Other platforms like Focusmate and StudyTogether also provide virtual co-working environments."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Why Studying on Camera Actually Helps You Get More Done",
    "description": "Discover the science behind why turning your camera on while studying boosts productivity. Learn about social accountability, social facilitation, body doubling, and pre-commitment strategies backed by research.",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-17",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/study-stream/why-studying-on-camera-helps/"
    },
    "keywords": ["studying on camera", "social accountability", "social facilitation", "body doubling", "Study With Me", "virtual study room", "online study", "focus productivity", "procrastination"],
    "about": [
      { "@type": "Thing", "name": "Social Accountability" },
      { "@type": "Thing", "name": "Social Facilitation" },
      { "@type": "Thing", "name": "Body Doubling" },
      { "@type": "Thing", "name": "Pre-commitment" },
      { "@type": "Thing", "name": "Study Productivity" }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Study Stream",
        "item": "https://csw.live/study-stream/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Why Studying on Camera Helps",
        "item": "https://csw.live/study-stream/why-studying-on-camera-helps/"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Why Studying on Camera Actually Helps You Get More Done"
        description="Discover the science behind why turning your camera on while studying boosts productivity. Learn about social accountability, social facilitation, body doubling, and pre-commitment strategies backed by research."
        canonicalUrl="/study-stream/why-studying-on-camera-helps/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/study-stream/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-10 font-body"
              onClick={() => window.scrollTo(0, 0)}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Study Stream
            </Link>
            
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium font-body">
                Research
              </span>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Calendar className="w-4 h-4" />
                <span>January 2025</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-body">
                <Clock className="w-4 h-4" />
                <span>6 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-10 leading-[1.1] tracking-tight">
              Why Studying on Camera Actually Helps You Get More Done
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-24 relative">
        <div className="container mx-auto px-4">
          <div className="flex gap-12 max-w-6xl mx-auto">
            {/* Main Content */}
            <div className="flex-1 max-w-4xl">
              {/* Table of Contents - Mobile */}
              <div className="lg:hidden mb-12">
                <BlogTableOfContents items={tocItems} />
              </div>

            {/* Lead Paragraph */}
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
              Let's be honest — studying alone sounds ideal, but rarely works out that way. You sit down with good intentions, open your laptop, and somehow end up checking messages, switching tabs, or "just taking a short break."
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              That doesn't mean you're lazy. It means your brain is doing what it's wired to do. Here's the interesting part: <strong>turning your camera on while studying actually helps</strong>, and there's real research behind it.
            </p>

            {/* Section 1: Social Accountability */}
            <div id="social-accountability" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    You Focus Better When Someone Can See You
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The biggest difference between studying alone and studying on camera is simple: <strong>you stop lying to yourself.</strong>
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The moment your camera is on, your behavior changes — even if no one is talking to you. Behavioral research shows that when people know their actions are observable, effort levels increase and distractions decrease, even without direct supervision (
              <a 
                href="https://www.aeaweb.org/articles?id=10.1257/aer.90.4.980" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Fehr & Gächter, 2000
              </a>
              ).
            </p>

            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-10">
              <div className="flex items-start gap-4">
                <Target className="w-8 h-8 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-display font-bold text-lg mb-2">Social Accountability</h3>
                  <p className="text-muted-foreground font-body">
                    This effect is called <em>social accountability</em>. It's not pressure — it's awareness. Studies on goal completion show that people are significantly more likely to follow through when commitments are visible to others, especially when there's a scheduled time involved (American Society for Training and Development, 2010).
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              That's why libraries feel more productive than bedrooms. It's not the silence — it's the presence.
            </p>

            {/* Section 2: Social Facilitation */}
            <div id="social-facilitation" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Seeing Others Study Makes Your Brain Fall in Line
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              There's a well-established concept in psychology called <strong>social facilitation</strong>: people perform familiar tasks better when others around them are doing the same thing.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Studying counts as a familiar task — reading, writing, reviewing notes. Research shows productivity improvements of <strong>16–32%</strong> when individuals work in the presence of peers (
              <a 
                href="https://www.journals.uchicago.edu/doi/10.1086/497351" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Falk & Ichino, 2006
              </a>
              ).
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Even virtual presence works. Large-scale studies of "Study With Me" (SWM) videos found that students report improved concentration and longer focus when studying alongside visible peers, even with no interaction (
              <a 
                href="https://doi.org/10.1016/j.compedu.2025.105488" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Liu, Deng & Zhou, 2026
              </a>
              ).
            </p>

            <div className="bg-muted/50 rounded-xl p-6 mb-12">
              <p className="text-lg font-body text-foreground/90 italic text-center">
                Your brain basically says: "Everyone else is working — so should I."
              </p>
            </div>

            {/* Section 3: Body Doubling */}
            <div id="body-doubling" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    Studying Together Doesn't Mean Being Distracted
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              A common fear is that studying with others leads to distraction. Surprisingly, research suggests the opposite — <strong>when interaction is removed</strong>.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              A controlled experimental study compared three situations:
            </p>

            <ol className="list-decimal list-inside space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4">
              <li>Studying alone</li>
              <li>Studying with a physically present partner</li>
              <li>Studying with a virtual "Study With Me" video</li>
            </ol>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              The results showed that students studying with a virtual companion performed <strong>as well or better</strong> than those studying with someone physically present, while feeling <strong>less pressure and stress</strong> (
              <a 
                href="https://doi.org/10.1016/j.compedu.2024.105243" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Ren, Reynolds & Rusk, 2025
              </a>
              ).
            </p>

            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-6 mb-12">
              <div className="flex items-start gap-4">
                <Brain className="w-8 h-8 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-display font-bold text-lg mb-2">Body Doubling</h3>
                  <p className="text-muted-foreground font-body">
                    This effect is often called <em>body doubling</em> — working alongside someone silently. You get motivation without conversation, and support without interruption.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4: Pre-commitment */}
            <div id="pre-commitment" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    It Makes Starting Way Easier (and Kills Procrastination)
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Procrastination isn't really about motivation — it's about starting.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Behavioral science shows that <strong>pre-commitment</strong> and <strong>scheduled intentions</strong> dramatically increase follow-through. When people decide <em>when</em> and <em>where</em> they'll work — especially with others — they're far more likely to begin and stick with it (
              <a 
                href="https://hbr.org/2002/08/creativity-under-the-gun" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Amabile et al., 2002
              </a>
              ).
            </p>

            <div className="bg-muted/50 rounded-xl p-6 mb-8">
              <h3 className="font-display font-bold text-lg mb-4">Live study rooms naturally create this structure:</h3>
              <ul className="space-y-2 text-foreground/90 font-body list-disc list-inside">
                <li>The session starts at a set time</li>
                <li>Others are already focused</li>
                <li>Leaving feels intentional, not accidental</li>
              </ul>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              That alone reduces last-minute cramming and improves consistency.
            </p>

            {/* Section 5: Stress Reduction */}
            <div id="stress-reduction" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    It Actually Lowers Stress
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Studying alone for long periods can feel isolating, which quietly drains motivation.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Research on study streaming communities shows that virtual co-study environments reduce feelings of loneliness and help students regulate stress, while maintaining focus (
              <a 
                href="https://doi.org/10.1016/j.compedu.2025.105488" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Liu et al., 2026
              </a>
              ).
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              Even minimal social presence — just knowing others are there — satisfies a basic psychological need for connection, which supports long-term motivation (Self-Determination Theory).
            </p>

            <div className="bg-muted/50 rounded-xl p-6 mb-12">
              <p className="text-lg font-body text-foreground/90 italic text-center">
                You're still studying quietly — you're just not doing it alone.
              </p>
            </div>

            {/* Section 6: CSW */}
            <div id="csw" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                    So Where Does CSW Come In?
                  </h2>
                </div>
              </div>
            </div>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              This is exactly the gap <strong><a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">csw.live</a></strong> is designed to fill.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              CSW brings together:
            </p>

            <ul className="space-y-2 text-lg font-body text-foreground/90 mb-8 ml-4 list-disc list-inside">
              <li>Camera-on study rooms</li>
              <li>Silent co-presence</li>
              <li>Structured focus timers</li>
              <li>Real students studying at the same time</li>
            </ul>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
              It doesn't force discipline. It makes discipline feel natural.
            </p>

            <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
              No noise, no obligation to talk, no algorithm pushing notifications. Just other people studying — and you.
            </p>

            {/* Conclusion */}
            <div id="takeaway" className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12 scroll-mt-28">
              <h2 className="text-2xl font-display font-bold mb-4 tracking-tight">The Takeaway</h2>
              <p className="text-lg leading-relaxed font-body text-foreground/90 mb-6">
                You don't need to study harder. You just need to study smarter — and sometimes that means giving yourself the same conditions that help everyone else focus: structure, presence, and a reason to stay.
              </p>
              <p className="text-lg leading-relaxed font-body text-foreground/90">
                If you've ever struggled to focus alone, it might not be a motivation problem. It might be an environment problem. And fixing that is easier than you think.
              </p>
            </div>

            {/* References */}
            <div id="references" className="border-t border-border pt-8 scroll-mt-28">
              <h2 className="text-2xl font-display font-bold mb-6 tracking-tight">References</h2>
              <ul className="space-y-4 text-foreground/80 font-body">
                <li>
                  <a 
                    href="https://www.aeaweb.org/articles?id=10.1257/aer.90.4.980" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Fehr, E., & Gächter, S. (2000). Cooperation and Punishment in Public Goods Experiments. American Economic Review, 90(4), 980-994.
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.journals.uchicago.edu/doi/10.1086/497351" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Falk, A., & Ichino, A. (2006). Clean Evidence on Peer Effects. Journal of Labor Economics, 24(1), 39-57.
                  </a>
                </li>
                <li>
                  <a 
                    href="https://doi.org/10.1016/j.compedu.2024.105243" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Ren, X., Reynolds, B., & Rusk, N. (2025). Virtual Study Companions: Effects on Academic Performance and Stress. Computers & Education.
                  </a>
                </li>
                <li>
                  <a 
                    href="https://doi.org/10.1016/j.compedu.2025.105488" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Liu, Y., Deng, H., & Zhou, X. (2026). Study Streaming Communities and Student Well-being. Computers & Education.
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.smithsonianmag.com/science-nature/can-virtual-coworking-platforms-make-us-more-productive-180983190/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Smithsonian Magazine (2023). Can Virtual Coworking Platforms Make Us More Productive?
                  </a>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <Link 
                to="/study-stream/"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-4 rounded-lg transition-all hover:scale-105"
              >
                Back to Study Stream
              </Link>
            </div>
            </div>

            {/* Desktop Sidebar TOC */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-28">
                <BlogTableOfContents items={tocItems} />
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default WhyStudyingOnCameraHelps;
